import {message, Modal, ModalProps, Skeleton} from 'antd';
import React from 'react';
import Style from './index.module.scss';
import FormRender, {useForm} from 'form-render';
import {Schema} from 'form-render/lib/type';
import useRequestFn from '../../../../../../../../Utils/Hooks/useRequestFn';
import {errorMessage} from '../../../../../../../../Utils/common';
import {
    buyService,
    BuyServiceProps,
    getServiceDetail,
    getTenantViewInfo,
    ServiceVersionProps,
} from '../../../../../service';
import SelectDict from '../../../../../../../../Features/Select/SelectDict';
import {useDict} from '../../../../../dict';
import dayjs from 'dayjs';
import useRequest from '../../../../../../../../Utils/Hooks/useRequest';
import {PlansType} from '../../../../../../../Plans/PlansAll/service';
import {getExpirationEndTime} from '../../../../../../../../Services/common';

interface Props extends ModalProps {
    customerId: string;
    onOk: () => void;
    id?: string;
}

const Index = ({customerId, onOk, id, ...props}: Props) => {
    const [data] = useRequest(() => getTenantViewInfo(customerId!));

    const dict = useDict();
    const form = useForm();
    const [save, {loading: saveLoading}] = useRequestFn(buyService);
    async function onFinish(formData: BuyServiceProps) {
        try {
            if (id) {
                formData.id = id;
            }
            await save({
                ...formData,
                customerId,
            });
            message.success('保存成功!');
            onOk();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const [defaultData, {loading: getLoading}] = useRequest(
        () => {
            if (id) {
                return getServiceDetail(id);
            } else {
                return Promise.resolve(undefined);
            }
        },
        {},
        [id],
    );

    const schema: Schema = {
        type: 'object',
        displayType: 'row',
        labelCol: 6,
        fieldCol: 15,
        properties: {
            serviceVersionId: {
                title: '销售方案',
                type: 'string',
                required: true,
                widget: 'select',
                disabled: id,
                props: {
                    options: dict.ServiceVersion.data,
                },
            },
            now: {
                default: `${data?.maxUserNumber}人`,
                title: '当前租户用户容量',
                type: 'string',
                readOnly: true,
            },
            expirationStartTime: {
                title: '服务开始时间',
                required: true,
                type: 'string',
                widget: 'datePicker',
                default: dayjs().format('YYYY-MM-DD'),
            },
            expirationEndTime: {
                title: '服务结束时间',
                required: true,
                type: 'string',
                widget: 'datePicker',
            },
            maxUserNumber: {
                title: '用户上限',
                type: 'number',
                description: '人',
                disabled: id,
                extra: '',
                props: {
                    min: 0,
                    max: 50000,
                },
            },
        },
    };

    const unitMap: Record<string, string> = {
        [PlansType.Sms]: '条',
        [PlansType.Storage]: 'GB',
    };

    async function onVersionChange(currentVersionId: string) {
        const currentVersion =
            dict.ServiceVersion.getCurrent<ServiceVersionProps>(
                currentVersionId,
            );

        form.setSchema({
            expirationStartTime: {
                hidden: [
                    PlansType.Expansion,
                    PlansType.Sms,
                    PlansType.Storage,
                ].includes(currentVersion.type),
            },
            expirationEndTime: {
                hidden: [
                    PlansType.Expansion,
                    PlansType.Sms,
                    PlansType.Storage,
                ].includes(currentVersion.type),
            },
            maxUserNumber: {
                title: getMaxUserNumberLabel(currentVersion.type),
                description: unitMap[currentVersion.type] || '人',
                extra:
                    currentVersion.type === PlansType.Expansion
                        ? `<div style='line-height: 1.5;margin-top: 15px'>
                                【说明】 <br />
                                当前 SAAS云资源承受的上限情况是每个租户的注册校友数均在 5 万以内，
                                <br />
                                如果租户注册校友数超过上述的阈值，那么云服务器及数据库资源亟需扩容。
                                <br />
                                请操作人员关注此限制，一旦系统给出提示“该租户注册校友数存在超过 5
                                万的风险， <br />
                                请联系技术提供方追加资源扩容。”，请第一时间联系技术支持方进行协商解决。
                           </div>`
                        : '',
            },
            now: {
                hidden: [PlansType.Sms, PlansType.Storage].includes(
                    currentVersion.type,
                ),
            },
        });

        form.setValueByPath('maxUserNumber', currentVersion.maxUserNumber);
    }

    function getMaxUserNumberLabel(type: string) {
        if (type === PlansType.Expansion) return '加购人数';
        if (type === PlansType.Sms) return '容量';
        if (type === PlansType.Storage) return '容量';
        return '用户上限';
    }

    async function setEndTime() {
        const expirationStartTime = form.getValueByPath('expirationStartTime');
        const serviceVersionId = form.getValueByPath('serviceVersionId');
        const currentVersion =
            dict.ServiceVersion.getCurrent<ServiceVersionProps>(
                serviceVersionId,
            );
        const time = await getExpirationEndTime(
            expirationStartTime,
            currentVersion.expireType,
        );
        form.setValueByPath('expirationEndTime', time);
    }

    const watch = {
        serviceVersionId: (value: string) => {
            onVersionChange(value);
            setEndTime();
        },
        expirationStartTime: () => {
            setEndTime();
        },
    };

    return (
        <Modal
            {...props}
            width={800}
            title={`${id ? '编辑' : '购买'}服务`}
            confirmLoading={saveLoading}
            onOk={form.submit}>
            <div className={Style.root}>
                <Skeleton active loading={getLoading}>
                    <FormRender
                        initialValues={defaultData}
                        watch={watch}
                        widgets={{SelectDict}}
                        form={form}
                        schema={schema}
                        onFinish={onFinish}
                        footer={false}
                    />
                </Skeleton>
            </div>
        </Modal>
    );
};

export default Index;
